import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TOUR_STEPS } from './steps';
import { decideStart, findPresentStep } from './tour';
import { TourOverlay } from './TourOverlay';

/**
 * Owns the feature tour's lifecycle for the app shell. The tour is a session
 * thing — nothing about it persists; the arrival overlay offers it once, and
 * the account menu's "Take the tour" re-runs it on request, so skipping is
 * always safe (that's the "come back later" path).
 */
interface TourContextValue {
  /** Whether the tour overlay is currently running. */
  active: boolean;
  /**
   * Between `start()` and the first spotlight: the walk is committed to, but the poll is
   * still deciding which step to open on (#510).
   *
   * Exposed because `active` is too late for anything that has to get out of the way. The
   * assistant panel reflows the content column, so it must be gone before anchors are
   * measured, and by the time `active` is true they already have been.
   */
  starting: boolean;
  /** Begin (or restart) the tour. Navigates home first — every anchor lives there. */
  start: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}

/** The tour's state for surfaces that merely *react* to it, and must still render where
 *  there is no tour at all (a page test, a surface outside the app shell). `useTour` stays
 *  the strict version for anything that drives the tour. */
export function useTourOptional(): TourContextValue | null {
  return useContext(TourContext);
}

/** An anchor counts only when it's actually visible — the desktop sidebar
 *  exists (display: none) at mobile widths and must not be toured there. */
function targetVisible(target: string): boolean {
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** A surface that renders one of the tour's anchors conditionally declares the
 *  wait with `data-tour-pending="<target>"` on something it always renders,
 *  and drops the attribute once the answer is known (whether or not the anchor
 *  ended up mounting). The tour holds its opening while any such claim stands
 *  — see decideStart() for why, and Dashboard.tsx for the one caller. */
function targetPending(target: string): boolean {
  return document.querySelector(`[data-tour-pending="${target}"]`) !== null;
}

/** ~3s of patience, in 100ms polls, before the tour opens with whatever it has. */
const MAX_TRIES = 30;

export function TourProvider({ children }: { children: ReactNode }) {
  const [index, setIndex] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const start = useCallback(() => {
    if (pathname !== '/') navigate('/');
    setIndex(null);
    setStarting(true);
  }, [pathname, navigate]);

  // Anchor discovery after start(): the Dashboard may still be mounting (we may
  // have just navigated there), so poll briefly rather than assuming the page is
  // settled on this exact frame. Two things end the wait — a visible anchor to
  // open on, with no conditional anchor still undecided; or patience running out
  // (~3s), after which we open on whatever is there and give up quietly if that
  // is nothing. Better no tour than a spotlight on nothing.
  useEffect(() => {
    if (!starting) return undefined;
    // Every anchor lives on the home surface, so decide nothing until we are
    // actually there. When start() navigates (the account menu's "Take the
    // tour" from another page), the route has not necessarily rendered by the
    // time this effect first runs — and the sidebar anchor is already visible
    // in the persistent shell, so deciding on that frame would open the tour on
    // a Dashboard that has not mounted and cannot yet stake its pending claim.
    // That is #510 again, on the very path most likely to hit it. The effect
    // re-runs when the location lands, and the poll's patience starts there.
    if (pathname !== '/') return undefined;
    let tries = 0;
    let timer = 0;
    const attempt = () => {
      const decision = decideStart(TOUR_STEPS, targetVisible, targetPending, tries++ >= MAX_TRIES);
      if (decision.action === 'wait') {
        timer = window.setTimeout(attempt, 100);
        return;
      }
      setStarting(false);
      if (decision.action === 'start') setIndex(decision.index);
    };
    attempt(); // check immediately — the usual case has the anchors already mounted
    return () => window.clearTimeout(timer);
  }, [starting, pathname]);

  const stop = useCallback(() => setIndex(null), []);

  const goNext = useCallback(() => {
    setIndex(i => {
      if (i === null) return null;
      // Past the last present step = the tour is done.
      return findPresentStep(TOUR_STEPS, i + 1, 1, targetVisible);
    });
  }, []);

  const goBack = useCallback(() => {
    setIndex(i => {
      if (i === null) return null;
      const prev = findPresentStep(TOUR_STEPS, i - 1, -1, targetVisible);
      return prev ?? i; // nothing behind us — stay put
    });
  }, []);

  return (
    <TourContext.Provider value={{ active: index !== null, starting, start }}>
      {children}
      {index !== null && (
        <TourOverlay
          steps={TOUR_STEPS}
          index={index}
          isPresent={targetVisible}
          onNext={goNext}
          onBack={goBack}
          onSkip={stop}
        />
      )}
    </TourContext.Provider>
  );
}
